//TODO : Refactor after ai
/**
 * Darstellung von Werten in Schweizer Schreibweise: Datum 06.10.2026,
 * Beträge „Fr. 1’611.60“, Ja/Nein statt true/false.
 */

import type { ToneColor } from "@shared/components/widgets/ooux";
import type { Feld, ObjektKlasse, Wert } from "./types";

const ZAHL = new Intl.NumberFormat("de-CH", { maximumFractionDigits: 2 });
const BETRAG = new Intl.NumberFormat("de-CH", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
const IST_BETRAG = /preis|betrag|volumen|kosten(?!satz)/i;
const IST_SATZ = /kostensatz/i;

/** Einheiten, die das Datenmodell nicht führt – im Prototyp aus der Feldbezeichnung abgeleitet. */
const EINHEIT: [RegExp, string][] = [
  [/temperatur/i, "°C"],
  [/luftfeuchtigkeit/i, "%"],
  [/gemessene (länge|breite)/i, "m"],
  [/dauer|stunden|pause|anfahrtszeit|leistung je stunde/i, "h"],
  [/ansatz|dosierung/i, "kg je Einheit"],
  [/zahlungsziel/i, "Tage"],
];

function datum(iso: string): string {
  const [d, zeit] = iso.split("T");
  const [y, m, t] = d.split("-");
  if (!y || !m || !t) return iso;
  return zeit ? `${t}.${m}.${y} ${zeit.slice(0, 5)}` : `${t}.${m}.${y}`;
}

export function formatWert(feld: Feld, wert: Wert | undefined): string {
  if (wert == null || wert === "") return "—";
  if (Array.isArray(wert)) return wert.join(", ");
  if (typeof wert === "boolean") return wert ? "Ja" : "Nein";
  if (feld.typ === "Datum" || feld.typ === "Datum und Uhrzeit")
    return datum(String(wert));
  if (typeof wert === "number") {
    if (IST_BETRAG.test(feld.label)) return `Fr. ${BETRAG.format(wert)}`;
    if (IST_SATZ.test(feld.label)) return `Fr. ${BETRAG.format(wert)} / h`;
    const einheit = EINHEIT.find(([muster]) => muster.test(feld.label))?.[1];
    if (einheit === "h" && /leistung je stunde/i.test(feld.label))
      return `${ZAHL.format(wert)} je h`;
    return einheit ? `${ZAHL.format(wert)} ${einheit}` : ZAHL.format(wert);
  }
  return String(wert);
}

/** Zustände, in denen am Datensatz nichts mehr zu tun ist – sie fallen aus dem offenen Vorrat. */
export const ERLEDIGTE_ZUSTAENDE = new Set([
  "Angenommen",
  "Abgelehnt",
  "Gewonnen",
  "Verloren",
  "Bezahlt",
  "Abgeschlossen",
  "Erledigt",
  "Verworfen",
  "Zugestellt",
  "Ausgeführt",
  "Ausführung abgesagt",
  "Bewertet",
  "Geklärt",
  "Überholt",
  "Ausgelistet",
]);

const NEGATIV = new Set([
  "Abgelehnt",
  "Verloren",
  "Verworfen",
  "Ausführung abgesagt",
  "Ausgelistet",
  "Überholt",
]);
const WARNUNG = new Set([
  "Änderung gewünscht",
  "Korrektur verlangt",
  "Gemahnt",
  "Gemeldet",
]);
const POSITIV = new Set([
  "Angenommen",
  "Gewonnen",
  "Bezahlt",
  "Abgeschlossen",
  "Erledigt",
  "Bestätigt",
  "Zugestellt",
  "Ausgeführt",
  "Bewertet",
  "Geklärt",
  "Aktiv",
  "Behoben",
]);

/** Ampelfarbe eines Zustands – Endzustände grün oder rot, Rückfragen gelb, der Rest blau. */
export function statusFarbe(
  klasse: ObjektKlasse,
  status: string | null
): ToneColor | undefined {
  if (!status) return undefined;
  if (NEGATIV.has(status)) return "error";
  if (WARNUNG.has(status)) return "warning";
  if (POSITIV.has(status)) return "success";
  return klasse.zustaende.indexOf(status) === 0 ? "info" : "brand";
}
